import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { trackWhatsappClick } from '@/utils/analytics';

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { state, dispatch } = useCart();
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  const handleWhatsApp = () => {
    trackWhatsappClick('header');
    window.open('https://wa.me/+51915976931?text=Hola Huaytanatura, quiero información sobre sus productos naturales y harinas andinas', '_blank');
  };

  return (
    <header className="sticky top-0 z-50 bg-card border-b border-border shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo Huaytanatura */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img
              src="/logos/favicon.jpg"
              alt=""
              aria-hidden="true"
              className="w-11 h-11 rounded-lg object-cover border border-border shadow-sm group-hover:shadow-md transition-shadow"
            />
            <div>
              <h1 className="font-serif font-bold text-2xl text-primary leading-none tracking-tight">Huaytanatura</h1>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1 hidden sm:block">
                Productos naturales y harinas andinas
              </p>
            </div>
          </Link>

          {/* Navigation - Desktop */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`font-medium transition-colors hover:text-primary ${isActive('/') ? 'text-primary' : 'text-foreground'
                }`}
            >
              Inicio
            </Link>
            <Link
              to="/tienda"
              className={`font-medium transition-colors hover:text-primary ${isActive('/tienda') ? 'text-primary' : 'text-foreground'
                }`}
            >
              Tienda
            </Link>
          </nav>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            {/* WhatsApp Button - Desktop */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleWhatsApp}
              className="hidden sm:flex items-center space-x-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground hover:scale-105 transition-all duration-200"
            >
              <MessageCircle className="w-4 h-4" />
              <span>WhatsApp</span>
            </Button>

            {/* Cart Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => dispatch({ type: 'TOGGLE_CART' })}
              className="relative"
              aria-label="Abrir carrito de compras"
            >
              <ShoppingCart className="w-4 h-4" aria-hidden="true" />
              {state.items.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {state.items.reduce((sum, item) => sum + item.quantity, 0)}
                </span>
              )}
            </Button>

            {/* Mobile Menu Button */}
            <Button
              variant="outline"
              size="sm"
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
            >
              {isMenuOpen ? <X className="w-4 h-4" aria-hidden="true" /> : <Menu className="w-4 h-4" aria-hidden="true" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-border bg-card">
            <nav className="flex flex-col space-y-4">
              <Link
                to="/"
                className={`font-medium transition-colors hover:text-primary ${isActive('/') ? 'text-primary' : 'text-foreground'
                  }`}
                onClick={() => setIsMenuOpen(false)}
              >
                Inicio
              </Link>
              <Link
                to="/tienda"
                className={`font-medium transition-colors hover:text-primary ${isActive('/tienda') ? 'text-primary' : 'text-foreground'
                  }`}
                onClick={() => setIsMenuOpen(false)}
              >
                Tienda
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={handleWhatsApp}
                className="w-fit border-primary text-primary hover:bg-primary hover:text-primary-foreground hover:scale-105 transition-all duration-200"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}